// src/homepage/Header.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const HeaderContainer = styled.header<{ scrolled: boolean }>`
    position: fixed;
    top: 2rem;
    left: 50%;
    transform: translateX(-50%);
    width: 95%;
    max-width: 1200px;
    padding: 1rem 2rem;
    background: ${props => props.scrolled
            ? 'rgba(255, 255, 255, 0.98)'
            : 'rgba(255, 255, 255, 0.95)'
    };
    backdrop-filter: blur(20px);
    border: 1px solid ${props => props.scrolled
            ? 'rgba(153, 1, 71, 0.15)'
            : 'rgba(153, 1, 71, 0.08)'
    };
    border-radius: 20px;
    z-index: 1000;
    transition: all 0.3s ease;
    box-shadow: ${props => props.scrolled
            ? '0 8px 40px rgba(153, 1, 71, 0.12)'
            : '0 4px 30px rgba(153, 1, 71, 0.08)'
    };

    @media (max-width: 768px) {
        top: 1rem;
        width: 90%;
        padding: 0.75rem 1.5rem;
    }
`;

const Nav = styled.nav`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Logo = styled(motion(Link))`
    font-size: 1.5rem;
    font-weight: 800;
    cursor: pointer;
    text-decoration: none;
    background: linear-gradient(135deg, #333333 0%, #990147 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    position: relative;
    padding: 0.5rem 0;

    &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: -10px;
        transform: translateY(-50%);
        width: 4px;
        height: 0;
        background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
        border-radius: 2px;
        transition: height 0.3s ease;
    }

    &:hover::before {
        height: 70%;
    }
`;

const NavLinks = styled.ul<{ isOpen: boolean }>`
    display: flex;
    list-style: none;
    gap: 2.5rem;
    margin: 0;
    padding: 0;
    align-items: center;

    @media (max-width: 968px) {
        position: absolute;
        top: calc(100% + 1rem);
        left: 50%;
        transform: translateX(-50%) ${props => props.isOpen ? 'scaleY(1)' : 'scaleY(0)'};
        width: 90%;
        background: rgba(255, 255, 255, 0.98);
        backdrop-filter: blur(20px);
        flex-direction: column;
        padding: 2rem;
        box-shadow: 0 20px 60px rgba(153, 1, 71, 0.15);
        border-radius: 16px;
        border: 1px solid rgba(153, 1, 71, 0.1);
        opacity: ${props => props.isOpen ? '1' : '0'};
        visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
        transition: all 0.3s ease;
        transform-origin: top center;
    }
`;

const NavLink = styled(motion.li)`
    position: relative;

    a {
        text-decoration: none;
        font-weight: 600;
        transition: all 0.3s ease;
        cursor: pointer;
        color: #555555;
        font-size: 0.95rem;
        position: relative;
        padding: 0.5rem 0;

        &:hover {
            color: #990147;
        }

        &.active {
            color: #990147;
            font-weight: 700;
        }
    }

    &::before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 2px;
        background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
        border-radius: 2px;
        transition: width 0.3s ease;
    }

    &:hover::before {
        width: 80%;
    }
`;

const MobileMenuButton = styled(motion.button)`
    display: none;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
    color: #333333;
    position: relative;
    z-index: 1001;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: rgba(153, 1, 71, 0.05);
    transition: all 0.3s ease;

    &:hover {
        background: rgba(153, 1, 71, 0.1);
    }

    @media (max-width: 968px) {
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

const Badge = styled.div`
    position: absolute;
    top: -8px;
    right: -8px;
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    color: white;
    font-size: 0.7rem;
    font-weight: 700;
    padding: 0.2rem 0.6rem;
    border-radius: 10px;
    box-shadow: 0 3px 10px rgba(153, 1, 71, 0.3);
`;

const Header: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();

    const navItems = [
        { label: 'Domov', path: '/' },
        { label: 'O mne', path: '/o-mne' },
        { label: 'Služby & Cenník', path: '/sluzby-cennik' },
        { label: 'Blog', path: '/blog' },
        { label: 'Kontakt', path: '/kontakt' }

    ];

    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 100;
            setScrolled(isScrolled);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleNavClick = (path: string) => {
        navigate(path);
        setIsMenuOpen(false);
    };

    const handleLogoClick = () => {
        navigate('/');
        setIsMenuOpen(false);
    };

    return (
        <HeaderContainer scrolled={scrolled}>
            <Nav>
                <Logo
                    to="/"
                    onClick={handleLogoClick}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    MUDr. Soňa Belanská
                </Logo>

                <NavLinks isOpen={isMenuOpen}>
                    {navItems.map((item, index) => (
                        <NavLink
                            key={item.path}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                        >
                            <a onClick={() => handleNavClick(item.path)}>
                                {item.label}
                            </a>
                        </NavLink>
                    ))}
                </NavLinks>

                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '2rem' }}>
                    <MobileMenuButton
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        whileTap={{ scale: 0.9 }}
                    >
                        {isMenuOpen ? '✕' : '☰'}
                    </MobileMenuButton>
                </div>
            </Nav>
        </HeaderContainer>
    );
};

export default Header;